import { useState } from 'react';

import { useGetUserConsent } from '@api/lendingAPI/lapiHooks';
import { datadogLogs } from '@datadog/browser-logs';
import { tracking } from '@iwoca/frontend-tracking-library';
import {
    IconAngleRight,
    IconCheckDefault,
    IconCopyOutline,
    IconShieldCheck
} from '@iwoca/icons';
import { Button, Link, Modal } from '@iwoca/orion';
import { FormattedMessage, useIntl } from 'react-intl';

import { usePostOpenBankingShareableLink } from '../../../api/lendingAPI/openBankingShareableLink/usePostOpenBankingShareableLink';
import { useUserConsentMutation } from '../../../api/lendingAPI/openBankingShareableLink/useUserConsentMutation';
import { getRuntimeEnvironment } from '../../../helpers/getRuntimeEnvironment';
import { getCurrentPathname } from '../../../iwRouter/LocationHelper';

const TRUE_LAYER_OPEN_BANKING_PROVIDER = 'truelayer_open_banking';
const COPIED_RESET_MS = 2000;

type Step = 1 | 2 | 3;

export const ShareableLinkModal = ({
    isOpen,
    onClose
}: {
    isOpen: boolean;
    onClose: () => void;
}) => {
    const intl = useIntl();
    const [step, setStep] = useState<Step>(1);
    const [linkCopied, setLinkCopied] = useState(false);

    const { userConsent } = useGetUserConsent();
    const { postUserConsent } = useUserConsentMutation();
    const { generateShareableLink, isLoading, isError, link } =
        usePostOpenBankingShareableLink();

    const handleAgreeAndGenerateLink = async () => {
        tracking.ampli.ctaClick({
            'CTA text': 'Agree and generate shareable link',
            URL: getCurrentPathname()
        });

        try {
            await generateShareableLink();
        } catch (error) {
            datadogLogs.logger.error(
                'Failed to generate share link',
                {},
                error as Error
            );
            return;
        }

        if (!userConsent) {
            postUserConsent({
                body: {
                    data: {
                        bundle_hash: import.meta.env.VITE_BUILD_HASH
                    }
                },
                provider: TRUE_LAYER_OPEN_BANKING_PROVIDER
            }).catch(() => {
                // useUserConsentMutation logs to datadog on its own
            });
        }

        setStep(3);
    };

    const handleClose = () => {
        setStep(1);
        setLinkCopied(false);
        onClose();
    };

    const handleCopy = () => {
        if (!link) return;
        navigator.clipboard
            .writeText(link)
            .then(() => {
                setLinkCopied(true);
                setTimeout(() => setLinkCopied(false), COPIED_RESET_MS);
                tracking.ampli.trueLayerGenerateShareLinkClicked({ url: link });
            })
            .catch(() => {});
    };

    const subTitle =
        step === 1
            ? intl.formatMessage({
                  id: '_.shareable_link_modal.subtitle.account',
                  defaultMessage:
                      'Authorise and share a secure link with someone else to link your account.'
              })
            : intl.formatMessage({
                  id: '_.shareable_link_modal.subtitle.documents',
                  defaultMessage:
                      'Authorise and share a secure link with someone else to complete your document requirements.'
              });

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            closeIcon
            headerBar
            title={intl.formatMessage({
                id: '_.shareable_link_modal.title',
                defaultMessage: 'Share a link for someone else to help'
            })}
            subTitle={subTitle}
            data-testid="shareable-link-modal"
        >
            <div className="fs-mask dd-privacy-mask">
                {step === 1 && <Step1 onContinue={() => setStep(2)} />}
                {step === 2 && (
                    <Step2
                        onAgree={handleAgreeAndGenerateLink}
                        isLoading={isLoading}
                        isError={isError}
                    />
                )}
                {step === 3 && (
                    <Step3
                        link={link}
                        linkCopied={linkCopied}
                        onCopy={handleCopy}
                    />
                )}
            </div>
        </Modal>
    );
};

const Step1 = ({ onContinue }: { onContinue: () => void }) => {
    const steps: Array<{
        title: React.ReactNode;
        description: React.ReactNode;
    }> = [
        {
            title: (
                <FormattedMessage
                    id="_.shareable_link_modal.step1.get_started.title"
                    defaultMessage="Get started"
                />
            ),
            description: (
                <FormattedMessage
                    id="_.shareable_link_modal.step1.get_started.description"
                    defaultMessage="Select ‘Get my secure shareable link’ below"
                />
            )
        },
        {
            title: (
                <FormattedMessage
                    id="_.shareable_link_modal.step1.authorise.title"
                    defaultMessage="Authorise"
                />
            ),
            description: (
                <FormattedMessage
                    id="_.shareable_link_modal.step1.authorise.description"
                    defaultMessage="Agree to share your banking data with iwoca"
                />
            )
        },
        {
            title: (
                <FormattedMessage
                    id="_.shareable_link_modal.step1.copy.title"
                    defaultMessage="Copy and paste the link"
                />
            ),
            description: (
                <FormattedMessage
                    id="_.shareable_link_modal.step1.copy.description"
                    defaultMessage="Share the secure link with your trusted third party however you like."
                />
            )
        },
        {
            title: (
                <FormattedMessage
                    id="_.shareable_link_modal.step1.confirmation.title"
                    defaultMessage="Wait for confirmation"
                />
            ),
            description: (
                <FormattedMessage
                    id="_.shareable_link_modal.step1.confirmation.description"
                    defaultMessage="Once they’ve linked your bank account we’ll let you know via email."
                />
            )
        }
    ];

    return (
        <>
            <ol className="mb-xl mt-xs list-none space-y-l p-0">
                {steps.map((s, i) => (
                    <li key={i} className="flex items-start">
                        <span className="mr-m flex size-[30px] shrink-0 items-center justify-center rounded-full bg-primary-20 text-m font-bold text-structure-100">
                            {i + 1}
                        </span>
                        <div className="flex flex-col">
                            <span className="text-m font-bold">{s.title}</span>
                            <span className="text-m text-structure-20">
                                {s.description}
                            </span>
                        </div>
                    </li>
                ))}
            </ol>
            <Button
                variant="denim"
                className="w-full rounded-2xl"
                onClick={() => {
                    tracking.ampli.ctaClick({
                        'CTA text': 'Get my secure shareable link',
                        URL: getCurrentPathname()
                    });
                    onContinue();
                }}
            >
                <FormattedMessage
                    id="_.shareable_link_modal.step1.cta"
                    defaultMessage="Get my secure shareable link"
                />
            </Button>
        </>
    );
};

const Step2 = ({
    onAgree,
    isLoading,
    isError
}: {
    onAgree: () => void;
    isLoading: boolean;
    isError: boolean;
}) => (
    <>
        <p className="mb-l mt-0 text-m">
            <FormattedMessage
                id="_.shareable_link_modal.step2.intro"
                defaultMessage="In order to generate your secure shareable link, we need you to provide consent to iwoca that we can use your banking data."
            />
        </p>
        <div className="mb-xl rounded-s bg-secondary-95 p-l">
            <h3 className="mb-xs mt-0 text-m font-bold">
                <FormattedMessage
                    id="_.shareable_link_modal.step2.details_title"
                    defaultMessage="What details am I sharing?"
                />
            </h3>
            <p className="mb-s mt-0 text-m">
                <FormattedMessage
                    id="_.shareable_link_modal.step2.details_subtitle"
                    defaultMessage="The following information will be shared with iwoca LTD:"
                />
            </p>
            <ul className="mb-l mt-0 list-disc space-y-xs pl-l text-m">
                <li>
                    <FormattedMessage
                        id="_.shareable_link_modal.step2.detail_full_name"
                        defaultMessage="Full name"
                    />
                </li>
                <li>
                    <FormattedMessage
                        id="_.shareable_link_modal.step2.detail_account_number"
                        defaultMessage="Account number and sort code"
                    />
                </li>
                <li>
                    <FormattedMessage
                        id="_.shareable_link_modal.step2.detail_balance"
                        defaultMessage="Balance"
                    />
                </li>
                <li>
                    <FormattedMessage
                        id="_.shareable_link_modal.step2.detail_transactions"
                        defaultMessage="Transactions direct debits and standing orders."
                    />
                </li>
            </ul>
            <div className="rounded-s bg-structure-100 p-m">
                <div className="mb-s flex items-center">
                    <IconShieldCheck className="mr-xs size-[20px] text-primary-40" />
                    <span className="text-m font-bold">
                        <FormattedMessage
                            id="_.shareable_link_modal.step2.encryption_title"
                            defaultMessage="Bank-grade encryption"
                        />
                    </span>
                </div>
                <p className="mb-s mt-0 text-s">
                    <FormattedMessage
                        id="_.shareable_link_modal.step2.encryption_text_1"
                        defaultMessage="We use a partner called Truelayer, to access the information needed to assess your ability to repay a loan."
                    />
                </p>
                <p className="mb-s mt-0 text-s">
                    <FormattedMessage
                        id="_.shareable_link_modal.step2.encryption_text_2"
                        defaultMessage="Truelayer needs your permission to access your information for 90 days."
                    />
                </p>
                <p className="m-0 text-s">
                    <FormattedMessage
                        id="_.shareable_link_modal.step2.encryption_text_3"
                        defaultMessage="Truelayer is FCA regulated, and won’t share or use your personal data for anything else."
                    />
                </p>
            </div>
        </div>
        <Button
            variant="denim"
            className="w-full rounded-2xl"
            onClick={onAgree}
            disabled={isLoading}
            loading={isLoading}
        >
            <FormattedMessage
                id="_.shareable_link_modal.step2.cta"
                defaultMessage="Agree and generate shareable link"
            />
        </Button>
        {isError && (
            <p className="m-s text-center text-s text-error">
                <FormattedMessage
                    id="_.shareable_link_modal.step2.error"
                    defaultMessage="Something went wrong. Please try again."
                />
            </p>
        )}
        <p className="mt-m text-center text-s text-structure-20">
            <FormattedMessage
                id="_.shareable_link_modal.step2.terms"
                defaultMessage="By selecting ‘Agree and generate shareable link’, you agree to {tosLink} and {privacyLink}."
                values={{
                    tosLink: (
                        <Link
                            href="https://truelayer.com/legal/enduser_tos/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <FormattedMessage
                                id="_.shareable_link_modal.step2.terms_tos"
                                defaultMessage="Truelayer’s Terms of Service"
                            />
                        </Link>
                    ),
                    privacyLink: (
                        <Link
                            href="https://truelayer.com/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <FormattedMessage
                                id="_.shareable_link_modal.step2.terms_privacy"
                                defaultMessage="Privacy Policy"
                            />
                        </Link>
                    )
                }}
            />
        </p>
    </>
);

const Step3 = ({
    link,
    linkCopied,
    onCopy
}: {
    link?: string;
    linkCopied: boolean;
    onCopy: () => void;
}) => {
    const signupBaseUrl =
        getRuntimeEnvironment() === 'production'
            ? 'https://www.iwoca.co.uk'
            : 'https://stage.iwoca-dev.co.uk';
    const previewHref = `${signupBaseUrl}/link-open-banking/preview-link-bank`;

    return (
        <>
            <div className="mb-l flex items-center gap-s">
                <IconCheckDefault className="size-xl text-success-mid" />
                <span>
                    <FormattedMessage
                        id="_.shareable_link_modal.step3.success"
                        defaultMessage="Thanks for agreeing. Your secure link is below."
                    />
                </span>
            </div>
            <div className="mb-l rounded-s bg-secondary-95 p-l">
                <h3 className="mb-xs mt-0 text-m font-bold">
                    <FormattedMessage
                        id="_.shareable_link_modal.step3.link_title"
                        defaultMessage="Your secure shareable link"
                    />
                </h3>
                <p className="mb-m mt-0 text-s text-structure-40">
                    <FormattedMessage
                        id="_.shareable_link_modal.step3.link_subtitle"
                        defaultMessage="(This link will be active for 90 days)"
                    />
                </p>
                <div className="flex items-center gap-m">
                    <span className="flex-1 truncate text-m text-structure-40">
                        {link ?? ''}
                    </span>
                    {linkCopied ? (
                        <div className="flex h-3xl shrink-0 items-center justify-center gap-xs px-l py-s text-m font-bold text-success-drk">
                            <FormattedMessage
                                id="_.shareable_link_modal.step3.copied"
                                defaultMessage="Link copied"
                            />
                            <IconCheckDefault className="size-l text-success-drk" />
                        </div>
                    ) : (
                        <Button
                            variant="denim"
                            className="flex shrink-0 items-center gap-xs rounded-2xl"
                            onClick={onCopy}
                        >
                            <FormattedMessage
                                id="_.shareable_link_modal.step3.copy"
                                defaultMessage="Copy link"
                            />
                            <IconCopyOutline className="size-l" />
                        </Button>
                    )}
                </div>
            </div>
            <Link
                href={previewHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-xs"
            >
                <FormattedMessage
                    id="_.shareable_link_modal.step3.preview"
                    defaultMessage="What will the recipient see when they access the link?"
                />
                <IconAngleRight className="size-l text-primary-40" />
            </Link>
        </>
    );
};
